import {
  CondenserType,
  ConfigurationType,
  LevelType,
  SectionType,
  SpaceType
} from 'data/types';
import {
  buildConcealedDuctLayoutsForConfiguration,
  ZoneConcealedDuctLayout
} from 'utils/concealed-duct-geometry';
import { getGridSpacesForLevel } from 'utils/grid';
import { showsLayoutDiagramDuctRuns } from 'utils/layout-diagram-duct-runs';
import { ZoneDuctModesMapType } from 'utils/zone-duct-modes';

type SnapshotSpace = Pick<
  SpaceType,
  'uniqueId' | 'label' | 'position' | 'vertices' | 'length' | 'width'
>;

type SnapshotSection = Pick<SectionType, 'uniqueId'> & {
  spaces: SnapshotSpace[];
};

type SnapshotLevel = Pick<LevelType, 'uniqueId' | 'label'> & {
  sections: SnapshotSection[];
};

type SnapshotCondenserProduct = Pick<
  NonNullable<CondenserType['selectedProduct']>,
  'photo' | 'coolingBTU' | 'name'
>;

type SnapshotCondenser = Pick<
  CondenserType,
  'uniqueId' | 'levelUniqueId' | 'name'
> & {
  position: NonNullable<CondenserType['position']>;
  placementType?: CondenserType['placementType'];
  selectedProduct?: SnapshotCondenserProduct;
};

type SnapshotPoint = { x: number; y: number };

export type SnapshotDuctRun = {
  kind: 'supply' | 'return';
  pathPx: SnapshotPoint[];
};

export type SnapshotDuctLayout = {
  zoneUniqueId: string;
  levelUniqueId: string;
  originPx: SnapshotPoint;
  runs: SnapshotDuctRun[];
};

export type QuoteFloorplanSnapshot = {
  levels: SnapshotLevel[];
  condensers: SnapshotCondenser[];
  ductLayouts?: SnapshotDuctLayout[];
};

function toSnapshotCondenser(
  condenser: CondenserType
): SnapshotCondenser | null {
  if (!condenser.position) return null;
  const product = condenser.selectedProduct;
  return {
    uniqueId: condenser.uniqueId,
    levelUniqueId: condenser.levelUniqueId,
    name: condenser.name,
    position: condenser.position,
    placementType: condenser.placementType,
    selectedProduct: product
      ? {
          name: product.name,
          photo: product.photo,
          coolingBTU: product.coolingBTU
        }
      : undefined
  };
}

function toSnapshotPoint(point: SnapshotPoint): SnapshotPoint {
  return { x: point.x, y: point.y };
}

function toSnapshotPath(path: SnapshotPoint[]): SnapshotPoint[] {
  return path.map(toSnapshotPoint);
}

function collectSnapshotDuctRuns(
  layout: ZoneConcealedDuctLayout
): SnapshotDuctRun[] {
  const passiveSpaceIds = new Set(
    layout.branches
      .filter((branch) => branch.servedPassively)
      .map((branch) => branch.spaceUniqueId)
  );

  const runs: SnapshotDuctRun[] = [];
  layout.branches.forEach((branch) => {
    if (
      !branch.sameLevelAsOrigin ||
      !branch.centroidPx ||
      branch.servedPassively
    ) {
      return;
    }
    runs.push({
      kind: 'supply',
      pathPx: toSnapshotPath(
        branch.pathPx ?? [layout.originPx, branch.centroidPx]
      )
    });
  });
  layout.additionalSupplyRuns.forEach((run) => {
    if (!run.sameLevelAsOrigin || passiveSpaceIds.has(run.spaceUniqueId)) {
      return;
    }
    runs.push({
      kind: 'supply',
      pathPx: toSnapshotPath(run.pathPx ?? [layout.originPx, run.endpointPx])
    });
  });
  const { returnRun } = layout;
  if (returnRun?.sameLevelAsOrigin) {
    runs.push({
      kind: 'return',
      pathPx: toSnapshotPath(
        returnRun.pathPx ?? [layout.originPx, returnRun.endpointPx]
      )
    });
  }
  layout.additionalReturnRuns.forEach((run) => {
    if (!run.sameLevelAsOrigin) return;
    runs.push({
      kind: 'return',
      pathPx: toSnapshotPath(run.pathPx ?? [layout.originPx, run.endpointPx])
    });
  });

  return runs;
}

function toSnapshotDuctLayout(
  zoneUniqueId: string,
  layout: ZoneConcealedDuctLayout,
  levels: LevelType[],
  includeRuns: boolean
): SnapshotDuctLayout | null {
  const level = levels[layout.originLevelIndex];
  if (!level) return null;

  return {
    zoneUniqueId,
    levelUniqueId: level.uniqueId,
    originPx: toSnapshotPoint(layout.originPx),
    runs: includeRuns ? collectSnapshotDuctRuns(layout) : []
  };
}

function buildSnapshotDuctLayouts(
  levels: LevelType[],
  configuration: ConfigurationType,
  zoneDuctModes?: ZoneDuctModesMapType
): SnapshotDuctLayout[] {
  const layouts = buildConcealedDuctLayoutsForConfiguration(
    configuration,
    levels,
    (levelIndex) => getGridSpacesForLevel(levels, levelIndex),
    zoneDuctModes
  );
  const includeRuns = showsLayoutDiagramDuctRuns(configuration);
  const out: SnapshotDuctLayout[] = [];
  layouts.forEach((layout, zoneUniqueId) => {
    const snapshot = toSnapshotDuctLayout(
      zoneUniqueId,
      layout,
      levels,
      includeRuns
    );
    if (snapshot) out.push(snapshot);
  });
  return out;
}

export function buildQuoteFloorplanSnapshot(
  levels: LevelType[],
  configuration: ConfigurationType,
  zoneDuctModes?: ZoneDuctModesMapType
): QuoteFloorplanSnapshot {
  const ductLayouts = buildSnapshotDuctLayouts(
    levels,
    configuration,
    zoneDuctModes
  );
  return {
    levels: levels.map((level) => ({
      uniqueId: level.uniqueId,
      label: level.label,
      sections: level.sections.map((section) => ({
        uniqueId: section.uniqueId,
        spaces: section.spaces.map((space) => ({
          uniqueId: space.uniqueId,
          label: space.label,
          position: space.position,
          vertices: space.vertices ?? undefined,
          length: space.length,
          width: space.width
        }))
      }))
    })),
    condensers: configuration.condensers
      .map(toSnapshotCondenser)
      .filter((c): c is SnapshotCondenser => c !== null),
    ...(ductLayouts.length > 0 ? { ductLayouts } : {})
  };
}

export function buildQuoteFloorplanSnapshotsByConfig(
  levels: LevelType[],
  configurations: ConfigurationType[],
  zoneDuctModes?: ZoneDuctModesMapType
): Map<string, QuoteFloorplanSnapshot> {
  return new Map(
    configurations.map((configuration) => [
      configuration.uniqueId,
      buildQuoteFloorplanSnapshot(levels, configuration, zoneDuctModes)
    ])
  );
}
